<template>
  <div class="flex justify-center otp-input-container" data-testid="otp_number">
    <input
      v-for="(_, index) in internalValue"
      @input="handleInput($event, index)"
      @keypress="onlyNumberKey($event)"
      @keydown="handleInputKeydown($event, index)"
      @focus="selectInputValue($event)"
      @paste="handlePaste($event)"
      :key="index"
      :ref="(el) => setInputRef(el, index)"
      :class="`${index + 1}`"
      :data-testid="`${index + 1}`"
      v-model="internalValue[index]"
      type="text"
      maxlength="1"
      placeholder="-"
      inputmode="numeric"
      class="border size-16 text-center m-0.5 text-lg border-gray-300 rounded"
    />
  </div>
</template>

<script setup lang="ts">
import { onlyNumberKey } from '@/utils/form.utils';
import { nextTick, ref, watch } from 'vue';

interface OtpProps {
  modelValue: string[];
}

const props = defineProps<OtpProps>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
}>();

const OTP_LENGTH = 5;
const inputs = ref<HTMLInputElement[]>([]);
const internalValue = ref<string[]>([...props.modelValue]);

const setInputRef = (el: HTMLInputElement | any, index: number) => {
  if (el) {
    inputs.value[index] = el;
  }
};

const selectInputValue = (event: Event) => {
  const target = event.target as HTMLInputElement;
  target.select();
};

const handleInput = async (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;

  const isNumeric = /^[0-9]+$/.test(value);

  if (isNumeric) {
    internalValue.value[index] = value;
    emit('update:modelValue', [...internalValue.value]);

    if (value && index < OTP_LENGTH - 1) {
      await nextTick();
      focusNextInput(index);
    }
  }
};

const focusNextInput = (index: number) => {
  inputs.value[index + 1]?.focus();
};

const focusPrevInput = (index: number) => {
  inputs.value[index - 1]?.focus();
};

const handleBackspace = async (event: KeyboardEvent, index: number) => {
  const target = event.target as HTMLInputElement;
  if (target.value !== '') {
    internalValue.value[index] = '';
    emit('update:modelValue', [...internalValue.value]);
    event.preventDefault();
  }
  await nextTick();
  focusPrevInput(index);
};

const handleInputKeydown = async (event: KeyboardEvent, index: number) => {
  switch (event.key) {
    case 'ArrowLeft':
      focusPrevInput(index);
      break;
    case 'ArrowRight':
      focusNextInput(index);
      break;
    case 'Backspace':
      handleBackspace(event, index);
      break;
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedValue = event.clipboardData?.getData('text');

  if (pastedValue) {
    const pastedValueArray = pastedValue.split('');
    const allNumeric = pastedValueArray.every((value) => /^[0-9]$/.test(value));

    if (allNumeric && pastedValueArray.length >= OTP_LENGTH) {
      for (let i = 0; i < internalValue.value.length; i++) {
        internalValue.value[i] = pastedValueArray[i];
      }
    }
    emit('update:modelValue', [...internalValue.value]);
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = [...newValue];
  },
);
</script>
